// Simple logging gate that respects a global showLog flag.

type ConsoleMethod = (...args: unknown[]) => void;

function safeGetWindow(): Window | null {
  try {
    // Accessing window can throw in some sandboxed envs
    return typeof window !== "undefined" ? window : null;
  } catch {
    return null;
  }
}

function shouldEnableLogs(): boolean {
  const w = safeGetWindow();
  if (!w) return false;

  // Allow opt-in via query (?showLog=1) for quick debugging sessions
  try {
    const url = new URL(w.location.href);
    const q = url.searchParams.get("showLog");
    if (q === "1" || q === "true") return true;
  } catch {
    // ignore
  }

  // Respect persisted preference
  try {
    const persisted = w.localStorage?.getItem("__pm_show_log__");
    if (persisted === "1") return true;
    if (persisted === "0") return false;
  } catch {
    // ignore
  }

  // Default global flag; fallback false
  return Boolean(globalThis.showLog);
}

export function initializeLoggingGate(): void {
  const w = safeGetWindow();
  if (!w) return; // only run on client

  // Initialize the global flag to false unless already set
  if (typeof globalThis.showLog === "undefined") {
    globalThis.showLog = false;
  }

  const originalLog: ConsoleMethod = console.log.bind(console);

  // Idempotency: avoid wrapping multiple times
  if ((console as any).__pmWrappedLog) return;

  const gatedLog: ConsoleMethod = (...args: unknown[]) => {
    if (shouldEnableLogs()) {
      originalLog(...args);
    }
  };

  (gatedLog as any).__pmWrapped = true;
  (console as any).__pmWrappedLog = true;
  console.log = gatedLog as typeof console.log;

  // Expose quick toggles for convenience
  try {
    (w as any).enableLogs = () => {
      try { w.localStorage?.setItem("__pm_show_log__", "1"); } catch {}
      globalThis.showLog = true;
    };
    (w as any).disableLogs = () => {
      try { w.localStorage?.setItem("__pm_show_log__", "0"); } catch {}
      globalThis.showLog = false;
    };
  } catch {
    // ignore
  }
}


